import React from 'react';
import { Icon, Modal } from 'Components';
import { useTranslation } from 'react-i18next';
import LMSButton from 'LearningManager/components/Button';
import styles from './BulkResetModal.module.scss';
import { pluralLabel } from 'LearningManager/helpers/role';

interface BulkResetModalProps {
	visible: boolean;
	selectedCount: number;
	userCount?: number;
	loading?: boolean;
	entityType?: 'assignment' | 'course';
	onCancel: () => void;
	onConfirm: () => void;
}

const BulkResetModal: React.FC<BulkResetModalProps> = ({
	visible,
	selectedCount,
	userCount,
	loading = false,
	entityType = 'assignment',
	onCancel,
	onConfirm
}) => {
	const { t } = useTranslation();

	const isAssignment = entityType === 'assignment';
	const hasNoUsers = !userCount;
	const userLabel = userCount === undefined ? '' : t(pluralLabel('learning.courseAssignments.bulkResetModal.users', userCount), { count: userCount })
	const selectedLabel = t(pluralLabel('learning.courseAssignments.bulkResetModal.assignments', selectedCount), { count: selectedCount })

	return (
		<Modal
			visible={visible}
			title={
				isAssignment
					? t('learning.courseAssignments.bulkResetModal.resetTraining')
					: t('learning.courseAssignments.bulkResetModal.resetCourses')
			}
			onCancel={loading ? undefined : onCancel}
			closable={!loading}
			maskClosable={!loading}
			size="sm"
			className={styles.modal}
			footer={
				<div className={styles.footer}>
					<LMSButton
						size="small"
						variant="borderless"
						type="default"
						onClick={onCancel}
						disabled={loading}
					>
						{t('learning.courseAssignments.bulkResetModal.cancel')}
					</LMSButton>
					<LMSButton
						type="primary"
						size="small"
						onClick={onConfirm}
						loading={loading}
						disabled={hasNoUsers}
					>
						{t('learning.courseAssignments.bulkResetModal.reset')}
					</LMSButton>
				</div>
			}
		>
			<div className={styles.body}>
				<p className={styles.text}>
					{isAssignment ? (
						<>
							{t(
								'learning.courseAssignments.bulkResetModal.assignmentProgress',
								{
									userCount: userLabel,
									selectedCount: selectedLabel
								}
							)}
						</>
					) : (
						<>
							{t('learning.courseAssignments.bulkResetModal.courseProgress',
								{
									userCount: userLabel
								}
							)}
						</>
					)}
				</p>
				<p className={styles.text}>
					{t('learning.courseAssignments.bulkResetModal.progressRemoved', {
						entity: t(
							`learning.courseAssignments.bulkResetModal.entity.${entityType}`
						)
					})}
				</p>
				<p className={styles.text}>
					{t('learning.courseAssignments.bulkResetModal.startAgain', {
						entity: t(
							`learning.courseAssignments.bulkResetModal.entity.${entityType}`
						)
					})}
				</p>
				<p className={styles.text}>{t('learning.courseAssignments.bulkResetModal.cannotBeUndone')}</p>
			</div>
		</Modal>
	);
};

export default BulkResetModal;